import anychart from 'Helpers/anychart.setup'
import { chartColor, setMaxAndInterval } from 'Helpers/chartService'
import React, { useRef, useEffect } from 'react'

import { uuid } from 'Helpers/uuid'
import { DateMapper } from 'mailup-web-components'
import { injectIntl } from 'react-intl'
import './AreaChart.scss'
import { constSelector } from 'recoil'

function formatDate(date) {
  const dateString = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .split('.')[0]
  return dateString
}

const chartId = uuid()

const AreaChart = ({ style, styleSeriesChart, data, intl }) => {
  const dateStart = data[0][0]
  const dateEnd = data[data.length - 1][0]
  const max = Math.max(...data.map(([, ...numbers]) => Math.max(...numbers)))
  const formattedData = data.map((x) => [formatDate(x[0]), ...x.slice(1)])

  const containerElement = useRef(null)

  const chart = anychart.area()

  chart.crosshair(true)
  chart.crosshair().yStroke(null)
  chart.crosshair().xLabel(null)
  chart.crosshair().yLabel(null)

  chart.crosshair().xStroke({
    thickness: 1,
    color: chartColor.Axis,
  })

  chart.yGrid().enabled(true).stroke({ dash: 7, color: chartColor.Axis }) // thickness: 2.5,

  chart.padding(20, 40, 20, 40)

  chart.yAxis().stroke({ color: '#FFFFFF' })

  chart.tooltip().useHtml(true)
  chart.tooltip().separator(false)

  chart
    .tooltip()
    .titleFormat(
      (t) =>
        `<div class="tooltip-title "> ${datetimeToSTring(data[t.points[0].index][0])}</div>`,
    )
  chart
    .tooltip()
    .format(
      ({ seriesName, value }) =>
        `<span class="tooltip-sub-title ${seriesName === styleSeriesChart[0].label ? 'opens' : 'clciks'}"> ${seriesName}: ${intl.formatNumber(value)}</span>`,
    )

  chart.yAxis().ticks().stroke({ thickness: 0 })
  chart
    .yAxis()
    .labels()
    .format(({ value }) => intl.formatNumber(value))

  chart.legend().fontFamily('Roboto')
  chart.xAxis().labels().fontFamily('Roboto')

  chart.yAxis().labels().fontFamily('Roboto')

  chart.yScale().ticks().allowFractional(false)
  chart.legend().enabled(true).position('bottom').padding().top(25)

  chart.credits().enabled(false)

  useEffect(() => {
    anychart.format.inputDateTimeFormat('yyyy-MM-ddTHH:mm:ss')
    const diffTime = Math.abs(dateEnd - dateStart)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    // create custom Date Time scale
    const dateTimeScale = anychart.scales.dateTime()
    const dateTimeTicks = dateTimeScale.ticks()

    chart.xScale(dateTimeScale)
    chart.contextMenu(false)
    dateTimeTicks.interval(0, 0, Math.ceil(diffDays / 5), 0)

    chart
      .xAxis()
      .labels()
      .format(({ tickValue }) => {
        return timestampToYMD(tickValue)
      })

    const yScale = chart.yScale()
    yScale.minimum(0)

    const { maxValue, interval } = setMaxAndInterval(max)
    yScale.maximum(maxValue)
    yScale.ticks().interval(interval)

    const gdata = anychart.data.set(formattedData)

    anychart.area()

    for (let i = 0; i < styleSeriesChart.length; i++) {
      const style = styleSeriesChart[i]
      const serie = chart.area(gdata.mapAs({ x: 0, value: i + 1 }))
      serie.name(style.label)
      serie.normal().stroke(style.stroke, 2)
      serie.normal().fill(style.fill, style.strokeOpacity)
      serie
        .hovered()
        .markers()
        .type('circle')
        .size(3)
        .stroke(style.stroke, 2)
        .fill(style.stroke, 2)
      serie.selected().markers().type('circle').size(3)
      serie.legendItem().iconFill(style.iconFill)
    }

    chart.container(chartId)
    chart.draw()

    return () => {
      chart.dispose()
    }
  }, [data])

  return (
    <div
      className="chart100"
      id={chartId}
      style={style}
      ref={containerElement}
    />
  )
}

function datetimeToSTring(date) {
  return DateMapper.dateToString(date)
}

function timestampToYMD(timestamp) {
  const dateString = new Date(timestamp).toISOString().split(/[T:-]/)
  return dateString
    .slice(0, 3)
    .map((token) => token.padStart(2, '0'))
    .join('-')
}

export default injectIntl(AreaChart)
